// The engine: one context, one atlas, three shared programs, the per-cell
// data textures, the camera target, the frame loop and the ordered layer
// slot table (the plan's A.2 and A.5).
//
// `createEngine` builds all of it and hands back an `Engine`. The wiring
// then fills the slots with real layers and calls `start()`. Nothing here
// knows what a fleet or a site is: the engine is the drawing machine and
// the layers are the game.

import { createGlBackend } from './gl/backend';
import { createGpuBackend } from './gpu/backend';
import { noteFaultContext } from '../net/faultreport';
import { pickBackend } from './gfx/select';
import { navigatorGpu, requestGpu } from './gpu/device';
import { readQueryKnobs } from './knobs';
import type {
  AttribSpec, DataFormat, DataTexture, Gfx, GfxBackend, MeshBuffer, Pipeline,
} from './gfx/types';
import { Atlas, atlasSpecFor, buildAtlasSpecs, type AtlasEntry } from './gfx/atlas';
import { QuadBatch, quadProgram } from './gfx/quads';
import { SegmentBatch, segmentProgram } from './gfx/segments';
import { CircleBatch, circleProgram } from './gfx/circles';
import { PaintSheet } from './gfx/paintsheet';
import type { SheetSink } from './layers/batches';
import {
  WorldPass, createBackgroundPass, createVignettePass, type WorldEnv,
} from './gfx/worldquad';
import type { ProgramSource } from './shaders/program';
import { Loop, type FastDebug, type FramePhase, type StatsSink } from './frame';
import { View } from './view';
import { NullLayer, type DrawCtx, type FrameInfo, type Layer } from './layer';
import { planeGeometry } from './terrain-prep';
import { flowGridSize } from './chart/pieces/current-field';
import {
  COL_PITCH, COLS, HEX_SIZE, ROWS, WORLD, WORLD_H, setGrid as setWorldGrid,
} from '../world';
import { FACTIONS } from '../gen/balance';
import { LIGHT } from '../identity/pick';
import { rgb } from '../render/color';
import {
  ACCENT_COLOR, NEUTRAL_COLOR, TERRAIN_FIELD_TEXEL_PX, TERRITORY_BORDER_PASSES,
} from '../gen/presentation';

/**
 * The layer slots, in draw order (the plan's A.5).
 *
 * Every slot exists from boot, even the ones the prototype defers, so the
 * order is stated once here and a later layer drops into its place without
 * anybody re-reading the table. The parity plan of 2026-09-04 states the
 * full order, which is the PixiJS client's own stack (`main.ts:595-596,
 * 619-631, 1270-1279`): the ambience straddles the world (fog and the
 * creatures under it, the haze, the snow and the vignette over it), the
 * hex views sit between the territory and the heatmap, the markers ride
 * above the effects, and the crosshair and the arrival cover everything.
 * The damage figures sit straight over the effects (R538): a figure must
 * read over the blast that made it, and under the order marks a player
 * gives.
 *
 * The chart furniture takes two slots (R548, §17). `chartsea` holds the
 * water pieces: they belong to the sea floor, so they draw under the beast,
 * the hex views, the heat map and every actionable object. `chartscreen`
 * holds the screen and selection pieces: they answer a question about a
 * hull or a hold, so they draw over the fleets and under the ambience.
 *
 * The player effects take two slots as well (R914, §6.6).
 * `holdeffects` sits right after `sites`, because a hold's mark always
 * draws UNDER the wakes, the hulls and the rounds — the owner's ruling.
 * `effects` sits right after `torpedoes`, because a hull's and a round's
 * mark draws OVER its own silhouette and under the blasts of `fx`. One
 * class fills both.
 *
 * The gun shots and the hits take ONE slot, `combat`, right after `fx`
 * (R1007, §6.3.3). A round in flight draws over every hull it passes, and a
 * hit mark draws over the blast of the same exchange; the damage figure still
 * stands over both, which is why `damage` follows.
 *
 * The Dredger harvesting looks take THREE slots (R1120, §6.2.1), because a
 * look draws through three hooks and the engine flushes one layer whole
 * before the next. `gatherunder` stands right before `sites`, so an
 * under-hull mark lies below the deposit diamond. `gathermid` stands right
 * after `holdeffects`, so a mark on the field lies over the diamond and under
 * every wake and hull. `gatherover` stands right after `fleets`, so a mark on
 * the hull lies over its own silhouette and under the rounds. One class fills
 * all three.
 *
 * The dead zone disc of the selected artillery hull takes `deadzone`,
 * right after `territory` (R1472). It is background: it lies on the water
 * and the sovereignty tint, and under the chart furniture, the grid, the
 * sonar, the sites, the wakes, the rings and the hulls. The fleet layer
 * fills it.
 *
 * The dashed outline of the hovered cell takes `hovercell`, right after
 * `heatmap` (R1875): it reads over the heat tint and under the sites and
 * the hulls, so the mark of the thing a peek names stands over it.
 *
 * The signature order looks take `signature`, right after `gatherover`
 * (R2408, §6.1.12): a look reads over the hulls and under the rounds, the
 * blasts and the hits.
 *
 * The depth tints take `depth`, right before `chartsea` (R2713,
 * §17.3.09): they lie on the water and the sovereignty tint, and every
 * figure of the chart, the soundings first, draws over them.
 *
 * The speed field of the current looks takes `speedfield`, right after
 * `depth` and before `chartsea` (R2827, §17.3.07): the wash lies over the
 * depth tints and under the streaks and every chart mark.
 *
 * The border lines of the territory take `borders`, right after
 * `speedfield` (R3241, §3): the wash stays in `territory`, under the depth
 * tints, and the line draws over the depth tints, the lanterns and the
 * speed field, so their alphas never dim or tint it.
 *
 * The hatch of unsurveyed water takes `survey`, right after `chartsea`
 * (R2626, §4.5): it lies over the depth tints of the chart, so a cell in
 * the abyss still reads as unsurveyed (round 088 card 06), and under the
 * beast, the hex views, the heat map and every object a player acts on.
 *
 * The drag order preview takes `orderdrag`, right under the crosshair
 * (R1208): it is an order the player gives now, so it reads over the whole
 * map, and an armed pick's mark never shares the glass with it.
 *
 * The draft course of the plot line takes `plot`, right after `orderdrag`
 * (R2589): it is an order the commander plots now, and the pick mark of
 * the crosshair stands over it.
 *
 * The work of an own Sapper takes `sapperwork`, right after `gatherover`
 * (R2657, §6.1.14): a lay or a collect look reads over the hull it works
 * from and under the rounds, the blasts and the hits.
 *
 * The sea objects of v2 phase 6 take `seaobjects`, right after `sites`
 * (R2657, §6.1.14): a sea mine and a deployable lie on the water, over the
 * deposit diamond and the hold, and under every hold effect, wake and hull
 * that passes them.
 *
 * The pirate dens take `dens`, between `sites` and `seaobjects` (R2720,
 * §6.1.13): a den lies on the sea bed, so a sea mine and every hull that
 * passes it draw over it.
 */
export const LAYER_SLOTS = [
  'background', 'fog', 'creatures', 'terrain', 'territory', 'deadzone', 'depth', 'speedfield', 'borders', 'chartsea', 'survey',
  'kraken',
  'hexfills', 'hexborders', 'heatmap', 'hovercell', 'quake', 'control', 'sonar',
  'gatherunder', 'sites', 'dens', 'seaobjects',
  'holdeffects', 'gathermid', 'wake',
  'fleets', 'monsters', 'gatherover', 'sapperwork', 'signature',
  'torpedoes', 'effects', 'fx', 'guard', 'combat', 'damage', 'markers', 'chartscreen', 'haze', 'snow',
  'vignette', 'orderdrag', 'plot', 'crosshair', 'inputs', 'spawn',
] as const;

/**
 * World px for one texel of the terrain plane (`uDist`).
 *
 * The plane carries four channels since R432 — the two Euclidean distance
 * transforms and the two fields — so it is `RGBA16F` and 14 MB for the core
 * world. The figure is the data layer's `presentation.terrain.field_texel_px`
 * and not a literal.
 */
export const DIST_STEP = TERRAIN_FIELD_TEXEL_PX;

/**
 * How far past the world rectangle the world quad reaches, in world px.
 *
 * The widest border pass is stroked on the line, so half of it stands
 * outside; twice the width is a pad no stroke can escape.
 */
export function worldPad(): number {
  return TERRITORY_BORDER_PASSES[0].width * 2;
}

/**
 * Whether the engine installs the vignette pass (§7). The paper of the
 * light identity takes none (R3295): a printed chart has no dark corners,
 * and the foot ink of the sheet already stands at the bottom of the sea
 * gradient. The slot then keeps its empty layer, so the paper pays for no
 * pass.
 */
export const VIGNETTE_ON: boolean = !LIGHT;

/** The eight palette entries every world shader reads (the plan's A.6). */
export function buildPalette(out = new Float32Array(32)): Float32Array {
  const put = (i: number, color: number, alpha: number) => {
    out.set(rgb(color), i * 4);
    out[i * 4 + 3] = alpha;
  };
  put(0, 0x000000, 0);                    // mask: unowned water, alpha 0
  for (let i = 0; i < 5; i++) {
    const f = FACTIONS[i];
    put(i + 1, f ? f.color : 0xffffff, 1);
  }
  put(6, ACCENT_COLOR, 1);
  put(7, NEUTRAL_COLOR, 1);
  return out;
}

/** A world pass wrapped as a layer, which is all rows 1, 4, 5, 6 and 16 are. */
export class WorldPassLayer implements Layer {
  private shown = true;
  constructor(readonly name: string, readonly pass: WorldPass, private readonly env: () => WorldEnv) {}
  update(_frame: FrameInfo): void { /* a shader pass advances nothing */ }
  draw(ctx: DrawCtx): void { this.pass.draw(ctx.cam, this.env()); }
  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }
  dispose(): void { this.pass.dispose(); }
}

export interface Engine {
  /** The drawing surface, whichever backend claimed the canvas. */
  readonly gfx: Gfx;
  readonly canvas: HTMLCanvasElement;
  /** What `render/camera.ts` pans and zooms. */
  readonly view: View;
  readonly atlas: Atlas;
  readonly loop: Loop;
  /** The slot table, in draw order. `setLayer` replaces one entry. */
  readonly layers: Layer[];
  /** What every world pass is told, rebuilt by `setGrid`. */
  readonly env: WorldEnv;

  readonly owners: DataTexture;
  readonly blocked: DataTexture;
  readonly heat: DataTexture;
  /** 1 on every cell the survey of the viewer's faction holds (R2626). */
  readonly survey: DataTexture;
  /** The depth band of every cell, 0 for none (R2713). */
  readonly depth: DataTexture;
  /** The speed field of the current looks, one texel a flow-grid node (R2827). */
  readonly speed: DataTexture;
  readonly dist: DataTexture;
  readonly compR: DataTexture;

  /** An atlas entry, baked on demand for a site key. */
  sprite(key: string): AtlasEntry;

  /** Applies the grid the server sent and recreates every data texture. */
  setGrid(cols: number, rows: number): void;
  /** Called after `setGrid` changed the grid, so the caller can refit. */
  onGrid: (() => void) | null;

  newQuads(capacity?: number): QuadBatch;
  /** A paint sheet: one 2D canvas, one texture and one quad (R2657). */
  newSheet(): SheetSink;
  newSegments(capacity?: number): SegmentBatch;
  newCircles(capacity?: number): CircleBatch;
  newWorldPass(program: ProgramSource): WorldPass;
  newMesh(floatsPerVertex: number, attribs: readonly AttribSpec[]): MeshBuffer;
  newDataTexture(
    name: string, width: number, height: number, format: DataFormat, linear?: boolean,
  ): DataTexture;

  layerNamed(name: string): Layer | undefined;
  setLayer(name: string, layer: Layer): void;

  start(): void;
  stop(): void;
  dispose(): void;
}

export interface EngineOpts {
  onStats?: StatsSink;
  maxFps?: number;
  /** The clock ms of one wall ms (`LoopOpts.clockScale`); 1 by default. */
  clockScale?: number;
  /** The four frame marks the shared perf harness reads (plan C.7). */
  onPhase?: (phase: FramePhase) => void;
  /** The client's own per-frame work, at the top of every frame. */
  onFrame?: (frame: FrameInfo) => void;
  /** The chrome's frame work while the cover state pauses the map, once
   *  a second (R1755). */
  onIdle?: () => void;
  /** Install the demo background and vignette passes. On by default. */
  demoPasses?: boolean;
  /** Publish `window.__fast`. On by default. */
  debugHandle?: boolean;
}

class FastEngine implements Engine {
  readonly gfx: Gfx;
  readonly view = new View();
  readonly atlas: Atlas;
  readonly loop: Loop;
  readonly layers: Layer[] = [];
  readonly env: WorldEnv;
  onGrid: (() => void) | null = null;

  owners!: DataTexture;
  blocked!: DataTexture;
  heat!: DataTexture;
  survey!: DataTexture;
  depth!: DataTexture;
  speed!: DataTexture;
  dist!: DataTexture;
  compR!: DataTexture;
  /** The ten planes above, in one list: what `setGrid` drops and a frame uploads. */
  private planes: DataTexture[] = [];

  private quadPipeline: Pipeline;
  private segmentPipeline: Pipeline;
  private circlePipeline: Pipeline;
  private batches: Array<{ dispose(): void }> = [];

  constructor(
    readonly canvas: HTMLCanvasElement,
    private readonly backend: GfxBackend,
    opts: EngineOpts,
  ) {
    this.gfx = backend.gfx;
    this.quadPipeline = backend.pipeline(quadProgram());
    this.segmentPipeline = backend.pipeline(segmentProgram());
    this.circlePipeline = backend.pipeline(circleProgram());
    // The atlas may grow to 4096 px where the device allows it: the chart
    // glyph cells of R548 are the one open-ended set it holds, and a full
    // atlas draws them as blanks. It starts small and grows on demand, so a
    // page that bakes few cells pays nothing for the ceiling.
    const maxTex = backend.maxTextureSize();
    this.atlas = new Atlas(
      (source) => backend.atlasTexture(source),
      { resolve: atlasSpecFor, maxSize: Math.min(4096, Math.max(2048, maxTex)) },
    );
    this.atlas.addAll(buildAtlasSpecs());

    this.env = {
      cols: COLS, rows: ROWS,
      hexSize: HEX_SIZE, colPitch: COL_PITCH,
      worldW: WORLD, worldH: WORLD_H,
      pad: worldPad(),
      palette: buildPalette(),
      timeS: 0,
      textures: {},
    };
    this.buildTextures();

    for (const name of LAYER_SLOTS) this.layers.push(new NullLayer(name));

    this.loop = new Loop({
      gfx: backend.gfx,
      view: this.view,
      layers: this.layers,
      beforeDraw: (frame) => this.beforeDraw(frame),
      onStats: opts.onStats,
      maxFps: opts.maxFps,
      clockScale: opts.clockScale,
      onPhase: opts.onPhase,
      onFrame: opts.onFrame,
      onIdle: opts.onIdle,
    });
    // A resize clears the canvas. A map the cover state paused draws one
    // frame then, so the strip under the tab bar keeps its picture (R1755).
    this.gfx.onResize(() => this.loop.redraw());

    if (opts.demoPasses !== false) {
      const make = (src: ProgramSource) => this.newWorldPass(src);
      this.setLayer('background', new WorldPassLayer(
        'background', createBackgroundPass(make), () => this.env));
      if (VIGNETTE_ON) {
        this.setLayer('vignette', new WorldPassLayer(
          'vignette', createVignettePass(make), () => this.env));
      }
    }
  }

  sprite(key: string): AtlasEntry { return this.atlas.get(key); }

  // -- the data textures ---------------------------------------------------

  private buildTextures(): void {
    const cols = COLS, rows = ROWS;
    this.owners = this.newDataTexture('owners', cols, rows, 'r8ui');
    this.blocked = this.newDataTexture('blocked', cols, rows, 'r8ui');
    this.heat = this.newDataTexture('heat', cols, rows, 'r8');
    // The survey plane (R2626): 1 on a surveyed cell. It starts all 1, so
    // a world with no survey on the wire hatches nothing, and the survey
    // layer stands invisible besides.
    this.survey = this.newDataTexture('survey', cols, rows, 'r8ui');
    this.survey.fill(1);
    // The depth plane (R2713): the band of each cell. It starts all 0, so
    // a world with no band field tints nothing.
    this.depth = this.newDataTexture('depth', cols, rows, 'r8ui');
    // The speed plane (R2827): one texel a node of the flow grid, not a
    // cell, the speed share and the wet mask. LINEAR, because the pass
    // reads it with a cubic B-spline in four bilinear taps. It starts all
    // 0, so a world with no current field washes nothing.
    const flow = flowGridSize(cols, rows);
    this.speed = this.newDataTexture('speed', flow.nx, flow.ny, 'rg16f', true);
    // The plane covers the whole rectangle the world quad spans, pad
    // included: `terrain-prep.ts` rasterises the same rectangle, and a
    // clamped edge texel would put the rim of a body that touches the world
    // edge in the wrong place. The size comes from `planeGeometry`, which
    // is the ONE place that arithmetic lives (R432).
    const plane = planeGeometry(cols, rows, DIST_STEP, worldPad());
    this.dist = this.newDataTexture('dist', plane.distW, plane.distH, 'rgba16f', true);
    this.compR = this.newDataTexture('compR', cols, rows, 'r16f');
    this.env.textures = {
      owners: this.owners,
      blocked: this.blocked,
      heat: this.heat,
      survey: this.survey,
      depth: this.depth,
      speed: this.speed,
      dist: this.dist,
      compR: this.compR,
    };
    this.planes = [this.owners, this.blocked, this.heat, this.survey, this.depth,
      this.speed, this.dist, this.compR];
  }

  setGrid(cols: number, rows: number): void {
    // The constructor built the planes, so an unchanged grid has nothing to do.
    if (!setWorldGrid(cols, rows)) return;
    for (const t of this.planes) t.dispose();
    this.env.cols = COLS;
    this.env.rows = ROWS;
    this.env.worldW = WORLD;
    this.env.worldH = WORLD_H;
    this.buildTextures();
    this.onGrid?.();
  }

  private beforeDraw(frame: FrameInfo): void {
    this.env.timeS = frame.timeS;
    for (const t of this.planes) t.upload();
  }

  // -- batches -------------------------------------------------------------

  private readonly makeInstances = (
    floats: number, attribs: readonly AttribSpec[], capacity: number,
  ) => this.backend.instances(floats, attribs, capacity);

  /** Keeps a batch for `dispose`, and hands it back. */
  private track<B extends { dispose(): void }>(b: B): B {
    this.batches.push(b);
    return b;
  }

  /** A quad batch on the shared program, whose sheet unit binds `bindAtlas`. */
  private quadBatch(bindAtlas: (unit: number) => void, capacity: number): QuadBatch {
    return this.track(new QuadBatch({
      gfx: this.gfx, pipeline: this.quadPipeline, instances: this.makeInstances, bindAtlas,
    }, capacity));
  }

  newQuads(capacity = 256): QuadBatch {
    return this.quadBatch((unit) => this.atlas.bind(unit), capacity);
  }

  newSheet(): SheetSink {
    return this.track(new PaintSheet({
      texture: (canvas) => this.backend.atlasTexture(canvas),
      quads: (bind) => this.quadBatch(bind, 4),
    }));
  }

  newSegments(capacity = 256): SegmentBatch {
    return this.track(new SegmentBatch({
      gfx: this.gfx, pipeline: this.segmentPipeline, instances: this.makeInstances,
    }, capacity));
  }

  newCircles(capacity = 256): CircleBatch {
    return this.track(new CircleBatch({
      gfx: this.gfx, pipeline: this.circlePipeline, instances: this.makeInstances,
    }, capacity));
  }

  newWorldPass(program: ProgramSource): WorldPass {
    return new WorldPass(this.gfx, this.backend.pipeline(program), program.name);
  }

  newMesh(floatsPerVertex: number, attribs: readonly AttribSpec[]): MeshBuffer {
    return this.backend.mesh(floatsPerVertex, attribs);
  }

  newDataTexture(
    name: string, width: number, height: number, format: DataFormat, linear?: boolean,
  ): DataTexture {
    return this.backend.dataTexture(name, width, height, format, linear);
  }

  // -- slots ---------------------------------------------------------------

  layerNamed(name: string): Layer | undefined {
    return this.layers.find((l) => l.name === name);
  }

  setLayer(name: string, layer: Layer): void {
    const i = this.layers.findIndex((l) => l.name === name);
    if (i < 0) throw new Error(`fast: ${name} is not a layer slot`);
    this.layers[i].dispose?.();
    this.layers[i] = layer;
    this.loop.trackLayer(layer.name);
  }

  // -- life ----------------------------------------------------------------

  start(): void { this.loop.start(); }
  stop(): void { this.loop.stop(); }

  dispose(): void {
    this.loop.dispose();
    for (const l of this.layers) l.dispose?.();
    for (const b of this.batches) b.dispose();
    this.atlas.dispose();
    this.gfx.dispose();
  }
}

/**
 * `?atlas` puts the packed sheet on top of the page (the plan's A.8), so an
 * operator sees which marks were baked and how they were cropped. It shows
 * the sheet the atlas holds NOW; a later grow builds a new canvas and the
 * shown one stops changing.
 */
function showAtlasOnRequest(atlas: Atlas): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  let wanted = false;
  try { wanted = new URLSearchParams(window.location.search).has('atlas'); } catch { return; }
  if (!wanted) return;
  const el = atlas.canvas;
  el.style.cssText = [
    'position:fixed', 'right:8px', 'bottom:8px', 'z-index:50',
    'width:min(40vw,512px)', 'height:auto',
    'image-rendering:pixelated', 'background:#111', 'pointer-events:none',
  ].join(';');
  document.body.appendChild(el);
}

/**
 * Builds the engine on a canvas.
 *
 * It returns a promise because the vendored face lands on the browser's own
 * schedule: every glyph cell — the badge digits and the damage figure's —
 * is baked at once with whatever face is there, and re-baked and patched
 * when the vendored face arrives (R427, R538).
 */
export async function createEngine(
  canvas: HTMLCanvasElement, opts: EngineOpts = {},
): Promise<Engine> {
  // The device is asked for FIRST and the canvas is claimed LAST: a canvas
  // that gave a `webgpu` context refuses `webgl2` after it.
  const knobs = readQueryKnobs();
  const pick = await pickBackend(knobs.renderer, () => requestGpu(navigatorGpu()));
  if (pick.kind === 'webgl2' && knobs.renderer === 'webgpu') {
    // A forced miss is said once. The stats line then tells the truth for
    // the rest of the session.
    // eslint-disable-next-line no-console
    console.warn(`fast: no WebGPU device (${pick.why}); drawing with WebGL2`);
  }
  // A missing WGSL twin THROWS here and does not fall back: the fall back is
  // for a browser with no device at all. `main.ts` catches the throw, logs it
  // and shows the one stop, so a half-landed branch never draws a blank page.
  const backend = pick.kind === 'webgpu'
    ? createGpuBackend(canvas, pick.bundle, knobs)
    : createGlBackend(canvas, knobs);
  const engine = new FastEngine(canvas, backend, opts);
  // An error report names the backend that draws (fault-reports.md §2).
  noteFaultContext({ backend: engine.gfx.backend });
  void engine.atlas.refreshGlyphCells();
  showAtlasOnRequest(engine.atlas);
  if (opts.debugHandle !== false && typeof window !== 'undefined') {
    const handle: FastDebug = {
      engine,
      camera: null,
      layers: engine.layers,
      stats: engine.loop.stats,
      atlas: engine.atlas,
      backend: engine.gfx.backend,
      loseContext: () => engine.gfx.loseContext(),
      restoreContext: () => engine.gfx.restoreContext(),
    };
    (window as unknown as { __fast: FastDebug }).__fast = handle;
  }
  return engine;
}
